import { type FC, type ReactNode, Component, type KeyboardEvent, createRef } from 'react';
import { createPortal } from 'react-dom';
import classnames from 'classnames';
import LegacyClose from 'dibs-icons/exports/legacy/Close.ts';
import Close from 'dibs-icons/exports/Close.ts';

import throttle from 'lodash/throttle.js';
import { Link } from 'dibs-elements/exports/Link.tsx';

import { TimeoutBar } from './TimeoutBar.tsx';
import { removeToast, getDomNode, popToast, updateToasterPosition } from './toaster.ts';
import { type ToastType, type ToastPosition } from './types.ts';
import stylesheet from './main.scss';

import dibsCss from 'dibs-css';

const DEFAULT_TIMEOUT = 5000;

// stageDirections
const ENTERING = 'entering';
const EXITING = 'exiting';
const CLOSING = 'closing';

type ExitStageDirections = 'exiting' | 'closing';

type StageDirections = 'entering' | ExitStageDirections;

export type ToastProps = {
    children: ReactNode;
    showCloseButton?: boolean;
    closeAfterTimeout?: boolean | number;
    type?: ToastType;
    position?: ToastPosition;
    isVisible?: boolean;
    onClose?: () => void;
    onContainerClick?: () => void;
    zIndex?: ZIndex;
    printHide?: boolean;
    hideTimeoutBar?: boolean;
    withCustomBackground?: boolean;
    className?: string;
    closeClassName?: string;
    timeoutBarClassName?: string;
    enterAnimationClassName?: string;
};

type ToastState = {
    stageDirection?: StageDirections | null;
    timeoutID?: NodeJS.Timeout | null;
    showTimeoutBar: boolean;
    duration?: number | null;
    toastNode?: HTMLElement | null;
    hasEntered: boolean;
};

type ZIndex = 'belowNav' | 'aboveOverlay';

type CloseButtonProps = { handleClose: () => void; type?: ToastType | null; className?: string };

const CloseButton: FC<CloseButtonProps> = ({ handleClose, type, className }) => {
    const isBasic = type === 'basic';
    return (
        <Link
            ariaLabel="Close"
            onClick={event => {
                event.stopPropagation();
                handleClose();
            }}
            className={classnames(
                isBasic ? stylesheet.closeButtonBasic : stylesheet.closeButton,
                !isBasic && type && stylesheet[type],
                className
            )}
        >
            {isBasic ? <Close variation="20" /> : <LegacyClose className={stylesheet.closeIcon} />}
        </Link>
    );
};

type AriaAttributes = {
    'aria-atomic': true;
    role?: 'alert' | 'status';
    'aria-live'?: 'assertive' | 'polite';
};

function getAriaAttributes(isVisible: boolean, type: ToastType | null): AriaAttributes {
    // roles "status" and "alert" act as dynamic triggers and should be added only
    // when the element is active
    // https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/Alert_Role
    const attrs: AriaAttributes = {
        'aria-atomic': true,
    };

    if (isVisible) {
        attrs.role = type === 'error' ? 'alert' : 'status';
        attrs['aria-live'] = type === 'error' ? 'assertive' : 'polite';
    }

    return attrs;
}

export class Toast extends Component<ToastProps, ToastState> {
    // redux connect breaks unless defaultProps has this type
    static defaultProps: Partial<ToastProps> | undefined = {
        showCloseButton: true,
        closeAfterTimeout: false,
        position: 'bottomRight',
        type: 'info',
        isVisible: true,
    };

    constructor(props: ToastProps) {
        super(props);
        this.throttledHandleScroll = throttle(this.handleScroll, 10, {
            trailing: true,
        });
    }

    state: ToastState = {
        timeoutID: null,
        showTimeoutBar: false,
        stageDirection: null,
        duration: null,
        toastNode: null,
        hasEntered: false,
    };

    componentDidMount(): void {
        const { position, isVisible = true, zIndex = 'belowNav', printHide } = this.props;
        const toastNode = getDomNode({
            position: position || 'bottomRight',
            isVisible,
            zIndexClass: stylesheet[zIndex],
            printHideClass: printHide ? stylesheet.printHide : undefined,
        });

        this.setState(
            {
                toastNode,
            },
            () => {
                if (this.props.isVisible) {
                    popToast(this.state.toastNode);
                    this.handleEnter();
                    this.applyScrollHandler();
                }
            }
        );
    }

    componentDidUpdate(prevProps: ToastProps): void {
        if (prevProps.isVisible !== this.props.isVisible) {
            if (this.props.isVisible) {
                popToast(this.state.toastNode);
                this.handleEnter();
                this.applyScrollHandler();
            } else {
                this.removeScrollHandler();
                if (
                    !this.state.stageDirection ||
                    ![EXITING, CLOSING].includes(this.state.stageDirection)
                ) {
                    this.handleClose(EXITING);
                }
            }
        }
        if (
            prevProps.closeAfterTimeout !== this.props.closeAfterTimeout &&
            this.props.closeAfterTimeout &&
            this.state.hasEntered
        ) {
            this.startCloseTimeout(this.props.closeAfterTimeout);
        }
    }

    componentWillUnmount(): void {
        if (this.state.timeoutID) {
            clearTimeout(this.state.timeoutID);
            this.setState({ timeoutID: null });
        }
        this.removeScrollHandler();
        removeToast(this.state.toastNode);
    }

    // used to prevent onClose from being called multiple times due to multiple transitionEnd events
    hasClosedRef = createRef();

    throttledHandleScroll: () => void;

    applyScrollHandler = (): void => {
        window.addEventListener('scroll', this.throttledHandleScroll);
    };

    removeScrollHandler = (): void => {
        window.removeEventListener('scroll', this.throttledHandleScroll);
    };

    handleEnter = (): void => {
        this.hasClosedRef.current = false;
        setTimeout(() => {
            this.setState({
                showTimeoutBar: !!this.props.closeAfterTimeout && !this.props.hideTimeoutBar,
                stageDirection: ENTERING,
            });
        }, 10);
    };

    // re-position the toaster dom node in case the navbar has changed height due to scrolling
    handleScroll = (): void => {
        updateToasterPosition();
    };

    handleTransitionEnd = (): void => {
        const { onClose, closeAfterTimeout } = this.props;
        const { stageDirection } = this.state;
        if (stageDirection === ENTERING) {
            this.setState({ hasEntered: true });
            if (closeAfterTimeout) {
                this.startCloseTimeout(closeAfterTimeout);
            }
        } else if (stageDirection === CLOSING || stageDirection === EXITING) {
            if (this.state.timeoutID) {
                clearTimeout(this.state.timeoutID);
            }
            this.setState({
                hasEntered: false,
                showTimeoutBar: false,
                timeoutID: null,
            });
            if (onClose && !this.hasClosedRef.current) {
                this.hasClosedRef.current = true;
                onClose();
            }
        }
    };

    handleClose = (stageDirection?: ExitStageDirections): void => {
        if (!stageDirection || ![EXITING, CLOSING].includes(stageDirection)) {
            stageDirection = CLOSING;
        }
        this.setState({ stageDirection });
    };

    startCloseTimeout = (timeoutDuration: number | boolean): void => {
        const timeoutDurationNumber =
            typeof timeoutDuration === 'number' ? timeoutDuration : DEFAULT_TIMEOUT;
        // timeout necessary timeout bar to render before giving it a timeout
        // otherwise timeout bar initially renders with width 0 instead of
        // transitioning to length 0
        const startTimer = (): NodeJS.Timeout =>
            setTimeout(() => {
                const timeoutID = setTimeout(() => {
                    this.setState({ timeoutID, stageDirection: EXITING });
                }, timeoutDurationNumber);
                this.setState({ timeoutID });
            }, 10);

        this.setState(
            { showTimeoutBar: !this.props.hideTimeoutBar, duration: timeoutDurationNumber },
            startTimer
        );
    };

    render(): ReactNode {
        const {
            position = 'bottomRight',
            closeAfterTimeout = false,
            type = 'info',
            showCloseButton,
            children,
            isVisible,
            withCustomBackground,
            className,
            closeClassName,
            timeoutBarClassName,
            enterAnimationClassName,
            onContainerClick,
        } = this.props;
        const isBasic = type === 'basic';
        const { duration, hasEntered, stageDirection, showTimeoutBar, timeoutID } = this.state;
        const dataTn = `${type}-toast-${isVisible ? 'visible' : 'invisible'}`;
        // live regions must be rendered before their content is rendered/changed
        // add live region attribute on isVisible, which is always true one render
        // before hasEntered || stageDirection === 'entering';
        // see https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/ARIA_Live_Regions
        const renderChildren = hasEntered || stageDirection === 'entering';
        const toast = (
            <div
                {...getAriaAttributes(!!isVisible, type)}
                className={classnames(
                    stylesheet.container,
                    stageDirection && stylesheet[stageDirection],
                    position && stylesheet[position],
                    type && stylesheet[type],
                    !isBasic && dibsCss.pMedium,
                    isBasic &&
                        classnames(
                            dibsCss.roundedXxsmall,
                            dibsCss.pySmall,
                            dibsCss.plSmall,
                            !withCustomBackground && dibsCss.bgWhite,
                            showCloseButton || !closeAfterTimeout
                                ? dibsCss.prLarge
                                : dibsCss.prSmall
                        ),
                    className,
                    stageDirection === 'entering' && enterAnimationClassName // Apply when entering
                )}
                onTransitionEnd={this.handleTransitionEnd}
                data-tn={dataTn}
                onClick={onContainerClick}
                onKeyDown={(event: KeyboardEvent) => {
                    if (event.key === 'Enter') {
                        onContainerClick?.();
                    }
                }}
                role="button"
                tabIndex={-1}
            >
                <div onTransitionEnd={e => e.stopPropagation()}>
                    {showTimeoutBar && duration && (
                        <TimeoutBar
                            className={stylesheet.timeoutContainer}
                            barClassName={isBasic ? timeoutBarClassName : undefined}
                            duration={duration}
                            inProgress={!!timeoutID && this.state.hasEntered}
                        />
                    )}
                    {(showCloseButton || !closeAfterTimeout) && (
                        <CloseButton
                            handleClose={this.handleClose}
                            type={type}
                            className={isBasic ? closeClassName : undefined}
                        />
                    )}
                    {renderChildren && children}
                </div>
            </div>
        );
        if (this.state.toastNode) {
            // this component will render normally in the React tree but
            // will render into the DOM as a child of the second argument.
            // see toaster.js for information on the toastNode used in the Toast.
            // read more @ https://reactjs.org/docs/portals.html
            return createPortal(toast, this.state.toastNode);
        }
        return null;
    }
}
